import styles from "./Login.module.css";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { useLoginUserMutation } from "src/services/authApi";
import { useAppDispatch } from "src/app/hooks";
import { setAuthInfo } from "src/features/authSlice";
import Cookies from "js-cookie";
import Warning from "src/components/Warning";
import { useForm } from "react-hook-form";
import SaveInProgress from "src/components/SaveInProgress";
import { HttpMethod, callApi } from "src/services/apiService";
import MfaSetupMain from "src/pages/MFA/SetupMain";
import platform from "platform";
import { getDeviceFingerprint } from "src/utils/fingerprint";
import { IAuthenticateResponse } from "src/interfaces/IAuthResponse";
import { classifyReturnUrl } from "src/utils/returnUrlUtils";
import sessionStorageService from "src/services/sessionStorageService";

type LoginFormFields = {
  acctId: string;
  username: string;
  password: string;
};

const Login = () => {
  const [deviceInfo, setDeviceInfo] = useState<string | null>(null);
  const [deviceId, SetDeviceId] = useState<string | null>(null);
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [showMFA, setShowMFA] = useState(false);
  const [mfaMethod, setMfaMethod] = useState<string | null>(null);
  const [validationCode, setValidationCode] = useState<string>("");
  const [showWarning, setShowWarning] = useState<string>("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const [searchParams] = useSearchParams();
  const [loginId, setLoginId] = useState<string>("");
  const [loginResponse, setLoginResponse] =
    useState<IAuthenticateResponse | null>(null);
  const [retryAttempt, setRetryAttempt] = useState<number>(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<LoginFormFields>({
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [loginUser] = useLoginUserMutation();

  useEffect(() => {
    const fetchDeviceId = async () => {
      const fingerprint = await getDeviceFingerprint();
      SetDeviceId(fingerprint);
    };
    fetchDeviceId();
    const os = platform.os?.family ?? "Unknown OS";
    const osVersion = platform.os?.version ?? "Unknown Version";
    const browser = platform.name ?? "Unknown Browser";
    const browserVersion = platform.version ?? "Unknown Version";

    setDeviceInfo(`${os} ${osVersion} | ${browser} ${browserVersion}`);
  }, []);

  useEffect(() => {
    const reason = decodeURIComponent(searchParams.get("reason") || "");
    if (reason) {
      switch (reason.toLowerCase()) {
        case "expired":
          setShowWarning("Your session has expired. \nPlease log in again.");
          break;
        case "selfregistered":
          setShowWarning(
            "Your new account was created. \nPlease login with the username and password you chose.",
          );
          break;
        default:
          setShowWarning("You have been logged out.");
          break;
      }
    }
  }, [searchParams]);

  useEffect(() => {
    setValue("acctId", Cookies.get("acctId") || "");
  }, [setValue]);

  const handleLogin = async (formData: LoginFormFields) => {
    setSaveInProgress(true);
    const { acctId, username, password } = formData;
    try {
      const response = await loginUser({
        acctId: acctId.toUpperCase(),
        username,
        password,
        deviceId: deviceId ?? "",
      }).unwrap();
      setLoginResponse(response);
      if (response.mustChangePassword) {
        // Password was valid but must be changed before continuing. The session is already
        // expired server-side; collect a new password, then the user re-logs in (which flows
        // into MFA setup for sysadmins on that second login).
        setLoginId(response.loginId);
        setShowChangePassword(true);
        setShowWarning(
          "Your password must be changed before you can continue.",
        );
      } else if (response.mfa && response.mfa !== "none") {
        // MFA required
        setRetryAttempt(0);
        setShowMFA(true);
        setMfaMethod(response.mfa);
        setLoginId(response.loginId);
        if (response.mfa === "setup") {
          setShowWarning(`You need to setup MFA before you can login.`);
        } else {
          if (response.mfa === "app") {
            setShowWarning(`Please enter the code from the authenticator.`);
          } else {
            setShowWarning(
              `A code has been sent to your ${response.mfa}<br />${response.mfaValue}.`,
            );
          }
        }
      } else {
        // No MFA required
        completeLogin(response);
      }
    } catch (err: any) {
      const errorMessage = err.data?.message || "Login failed.";
      setShowWarning(errorMessage);
    } finally {
      setSaveInProgress(false);
    }
  };

  const handleValidation = async () => {
    setSaveInProgress(true);

    try {
      const apiUrl = `mfa/VerifyCode`;
      const requestBody = {
        loginId: loginId,
        type: mfaMethod,
        code: validationCode,
        deviceInfo: deviceInfo,
        deviceId: deviceId,
        trustDevice: rememberDevice,
      };
      const response = await callApi(apiUrl, HttpMethod.POST, requestBody);
      if (response && response.type.toLowerCase() === "success") {
        if (loginResponse) {
          completeLogin(loginResponse);
        }
      } else if (response && response.type.toLowerCase() === "failed") {
        setRetryAttempt(retryAttempt + 1);
        setShowWarning(
          "Validation code is incorrect or expired.<br />Please try again.",
        );
      }
    } catch (error) {
      setShowWarning("Unable to validate. Please try again.");
    } finally {
      setSaveInProgress(false);
      if (retryAttempt >= 3) {
        setShowWarning("You have exceeded the maximum number of attempts.");
        clearLoginState();
      }
    }
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 10) {
      setShowWarning("Password must be at least 10 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setShowWarning("The passwords do not match.");
      return;
    }

    setSaveInProgress(true);
    try {
      const response = await callApi(
        "PasswordReset/ChangeExpiredPassword",
        HttpMethod.POST,
        { loginId, newPassword },
      );
      if (response && response.message === "success") {
        clearLoginState();
        setValue("password", "");
        setShowWarning("Password updated. Please sign in with your new password.");
      } else {
        // Server-side rejection (e.g. breached-password check or expired session).
        setShowWarning(response?.message || "Unable to change password. Please try again.");
      }
    } catch (error) {
      setShowWarning("Unable to change password. Please try again.");
    } finally {
      setSaveInProgress(false);
    }
  };

  const handleBackButtonClick = () => {
    clearLoginState();
    setShowWarning("Login attempt cancelled.");
  };

  const resendCode = async () => {
    setSaveInProgress(true);

    try {
      const apiUrl = `mfa/ResendVerificationCode?loginId=${loginId}&type=${mfaMethod}`;
      const response = await callApi(apiUrl, HttpMethod.POST);
      if (response && response.type.toLowerCase() === "success") {
        setRetryAttempt(0);
        setShowWarning(
          `Verification code has been resent to your ${mfaMethod}.`,
        );
      } else if (response && response.type.toLowerCase() === "failed") {
        setShowWarning(
          "There was an issue sending the code. Please try again.",
        );
      }
    } catch (error) {
      setShowWarning("Unable to validate. Please try again.");
    } finally {
      setSaveInProgress(false);
      if (retryAttempt >= 3) {
        setShowWarning("You have exceeded the maximum number of attempts.");
        clearLoginState();
      }
    }
  };

  const onCompleteSetup = () => {
    if (loginResponse) {
      completeLogin(loginResponse);
    }
  };

  const completeLogin = (data: IAuthenticateResponse) => {
    dispatch(
      setAuthInfo({
        acctId: data.acctId || "",
        acctName: data.acctName,
        username: data.username || "",
        fullName: data.fullName || "",
        quickScrub: data.quickScrub,
        role: data.role.toString(),
        isSysadmin: data.isSysadmin,
        hasSalesReportAccess: data.hasSalesReportAccess ?? false,
        loginId: data.loginId,
        csrfToken: data.csrfToken,
        hasTrainingFeature: data.hasTrainingFeature,
        homePage: data.homePage,
        landingPage: data.landingPage,
        acctMessage: data.acctMessage,
        userEmail: data.userEmail,
        isEmailVerified: data.isEmailVerified,
        isNXXG: data.isNXXG,
        expiresAt: data.expiresAt,
      }),
    );

    const defaultPage = data.role === 4 ? "/login/self-register" : data.landingPage || "/home";

    // Where to land after login. Priority:
    //   1. returnUrl / afterLoginPage query param -- set when the user was
    //      bounced to /login from a protected route (PrivateRoute /
    //      LoadingToRedirect) or an expired-session 401 (apiService). Tied to
    //      the navigation that was actually interrupted, so it cannot go stale.
    //   2. A session-scoped "last location" breadcrumb -- lets a same-session
    //      re-login resume where the user was. This used to live in
    //      localStorage, where it survived logout and browser restarts and
    //      hijacked deliberate fresh logins, dropping users onto a page from a
    //      prior session (warm-up jobs, CCCREM, the classic app). It now lives
    //      in sessionStorage, so a fresh login (new tab / next day) skips it
    //      and lands on home. Stale localStorage breadcrumbs are ignored.
    //   3. defaultPage -- "/home", from which Home.tsx routes role-1 users on
    //      to their server-configured homePage.
    const lastLocationKey = `lastLocation_${data.acctId}_${data.username}`;
    let lastLocation = sessionStorageService.getItem<string>(lastLocationKey);
    if (lastLocation?.toLowerCase().startsWith("/login")) {
      sessionStorageService.removeItem(lastLocationKey);
      lastLocation = null;
    }

    // Decode the returnUrl in case it's still encoded from the redirect.
    // Relative paths navigate within the SPA. Absolute URLs are allowed only
    // when the host is dncscrub.com or a subdomain (e.g. core.dncscrub.com) --
    // those trigger a full-page navigation since react-router cannot cross
    // hosts. Anything else is rejected to prevent open-redirect abuse.
    const rawReturnUrl =
      searchParams.get("returnUrl") ?? searchParams.get("afterLoginPage");
    const returnUrl = rawReturnUrl ? decodeURIComponent(rawReturnUrl) : null;
    const returnUrlKind = classifyReturnUrl(
      returnUrl,
      window.location.hostname,
    );
    if (returnUrlKind === "relative" && data.role !== 4) {
      navigate(returnUrl!);
    } else if (returnUrlKind === "absolute" && data.role !== 4) {
      window.location.href = returnUrl!;
    } else if (lastLocation && data.role !== 4) {
      navigate(lastLocation);
    } else {
      navigate(defaultPage);
    }
  };

  const clearLoginState = () => {
    setShowMFA(false);
    setValidationCode("");
    setMfaMethod(null);
    setShowChangePassword(false);
    setNewPassword("");
    setConfirmPassword("");
  };

  return (
    <form
      onSubmit={handleSubmit(
        showChangePassword
          ? () => handleChangePassword()
          : showMFA
            ? handleValidation
            : handleLogin,
      )}
    >
      <Warning message={showWarning} />
      {!showMFA && !showChangePassword && (
        <>
          <input
            {...register("acctId", {
              required: "Account ID is required",
              onChange: (e) => setValue("acctId", e.target.value.toUpperCase()),
            })}
            className={`${styles["form-control"]} ${
              errors.acctId ? styles["error-input-box"] : ""
            }`}
            placeholder="Account"
          />
          {errors.acctId?.message && (
            <div className={styles["error-message"]}>
              {errors.acctId.message}
            </div>
          )}

          <input
            {...register("username", { required: "Username is required" })}
            className={`${styles["form-control"]} ${
              errors.username ? styles["error-input-box"] : ""
            }`}
            placeholder="Username"
            autoComplete="username"
          />
          {errors.username?.message && (
            <div className={styles["error-message"]}>
              {errors.username.message}
            </div>
          )}

          <div className={styles["password-container"]}>
            <input
              {...register("password", { required: "Password is required" })}
              className={`${styles["form-control"]} ${
                errors.password ? styles["error-input-box"] : ""
              }`}
              placeholder="Password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
            />
            <button
              type="button"
              className={styles["password-toggle"]}
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <i className={`bi bi-${showPassword ? "eye-slash" : "eye"}`}></i>
            </button>
          </div>
          {errors.password?.message && (
            <div className={styles["error-message"]}>
              {errors.password.message}
            </div>
          )}
        </>
      )}

      {showChangePassword && (
        <>
          <div className={styles["password-container"]}>
            <input
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className={styles["form-control"]}
              placeholder="New password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
            />
            <button
              type="button"
              className={styles["password-toggle"]}
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <i className={`bi bi-${showPassword ? "eye-slash" : "eye"}`}></i>
            </button>
          </div>
          <input
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className={styles["form-control"]}
            placeholder="Confirm new password"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
          />
          <button
            type="button"
            className={styles["login-btn"]}
            onClick={handleChangePassword}
            disabled={saveInProgress}
          >
            <SaveInProgress isVisible={saveInProgress} loadingText="" />
            {!saveInProgress && <>Set new password</>}
          </button>
          <div className="mt-2 mb-2">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleBackButtonClick();
              }}
            >
              Back to login
            </a>
          </div>
        </>
      )}

      {mfaMethod === "setup" && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 1040,
            }}
          ></div>
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "#fff",
              padding: "20px",
              borderRadius: "8px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              zIndex: 1050,
            }}
          >
            <MfaSetupMain loginId={loginId} onCompleteSetup={onCompleteSetup} />
          </div>
        </>
      )}
      {showMFA && mfaMethod !== "setup" && (
        <>
          <input
            type="text"
            autoComplete="one-time-code"
            value={validationCode}
            onChange={(e) => setValidationCode(e.target.value)}
            className={`${styles["form-control"]}`}
            placeholder="Enter validation code"
          />
          {deviceId && deviceId.trim() !== "" && (
            <>
              <div className="mt-2 text-center">
                <input
                  type="checkbox"
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                  id="rememberDevice"
                  className="form-check-input"
                />
                <label
                  htmlFor="rememberDevice"
                  className="form-check-label ps-2"
                  style={{ paddingTop: "1px" }}
                >
                  <small>Remember validation for 30 days</small>
                </label>
              </div>
            </>
          )}

          {mfaMethod !== "app" && (
            <div className="mt-2">
              Didn't receive a code?{" "}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  resendCode();
                }}
              >
                Resend code
              </a>
            </div>
          )}
          <div className="mt-2 mb-2">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleBackButtonClick();
              }}
            >
              Back to login
            </a>
          </div>
        </>
      )}

      {!showChangePassword && (
        <button className={styles["login-btn"]} type="submit">
          <SaveInProgress isVisible={saveInProgress} loadingText="" />
          {!saveInProgress && <>{showMFA ? "Validate" : "Login"}</>}
        </button>
      )}
      {!showMFA && !showChangePassword && (
        <div className="mt-3 mb-0">
          <Link to="/login/reset-your-password">Forgot your password?</Link>
        </div>
      )}
    </form>
  );
};

export default Login;
